import moment from "moment";
import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { isMobile } from "../../Utils/Utils";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface LiveCountdownProps {
  startTime: string;
  messageBefore?: string;
  messageAfter?: string;
}

export default function LiveCountdown({
  startTime,
  messageBefore,
  messageAfter,
}: LiveCountdownProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const eventTime = moment.utc(startTime).local();
      const now = moment();
      const duration = moment.duration(eventTime.diff(now));

      return {
        days: Math.max(0, duration.days()),
        hours: Math.max(0, duration.hours()),
        minutes: Math.max(0, duration.minutes()),
        seconds: Math.max(0, duration.seconds()),
      };
    };

    // Set initial value immediately
    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime]);

  const formatTime = (value: number) => (value < 10 ? `0${value}` : value);

  if (timeLeft === null) {
    return <Box height={120} />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        p: isMobile() ? 0.5 : 3,
        mx: "auto",
        my: 2,
      }}
    >
      {messageBefore && (
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "text.secondary" }}
        >
          {messageBefore}
        </Typography>
      )}

      <Box display="flex" gap={2} mt={2}>
        {timeLeft.days > 0 && (
          <TimeUnit
            value={formatTime(timeLeft.days)}
            unit="ZILE"
            color="#4f46e5"
          />
        )}
        <TimeUnit
          value={formatTime(timeLeft.hours)}
          unit="ORE"
          color="#10b981"
        />
        <TimeUnit
          value={formatTime(timeLeft.minutes)}
          unit="MINUTE"
          color="#f59e0b"
        />
        <TimeUnit
          value={formatTime(timeLeft.seconds)}
          unit="SECUNDE"
          color="#ef4444"
        />
      </Box>

      {messageAfter && (
        <Typography
          variant="body1"
          sx={{
            mb: 2,
            fontSize: "1.1rem",
            color: "#37474f",
            lineHeight: 1.6,
            width: "100%",
          }}
        >
          {messageAfter}
        </Typography>
      )}
    </Box>
  );
}

function TimeUnit({
  value,
  unit,
  color,
}: {
  value: string | number;
  unit: string;
  color: string;
}) {
  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1,
          p: isMobile() ? 0.5 : 2,
          minWidth: 70,
          textAlign: "center",
          boxShadow: 1,
          borderBottom: `4px solid ${color}`,
        }}
      >
        <Typography variant="h4" fontWeight={700}>
          {value}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        sx={{
          mt: 1,
          fontWeight: 700,
          color,
          textTransform: "uppercase",
          letterSpacing: 1,
        }}
      >
        {unit}
      </Typography>
    </Box>
  );
}
